// Copyright (c) Docugami, Inc. All rights reserved.

import { MutableRefObject } from "react";
import { ResizeComponent } from "sdv/components/ResizeComponent";
import { ComponentId } from "sdv/engine/Component";
import { IEntityManager } from "sdv/engine/Entity";
import { ActionId } from "sdv/input/Actions";
import { IInput } from "sdv/input/InputController";
import { Mode } from "sdv/Mode";
import { IResources } from "sdv/Resources";
import { worldToViewSpace } from "sdv/Space";
import { animationCurves, AnimationCurveType, interpolate } from "utils/Easings";
import { clamp } from "utils/MathUtils";
import { ISystem, Queries } from "../engine/Engine";
import { ViewComponent } from "sdv/components/ViewComponent";

interface AutoScrollConfig {
    minSpeed: number;
    maxSpeed: number;
    scrollAreaCanvasEdgeOffset: number;
    scrollArea: number;
    animationCurve: AnimationCurveType;
}

const defaultConfig: AutoScrollConfig = {
    minSpeed: 150,
    maxSpeed: 2500,
    scrollAreaCanvasEdgeOffset: 30,
    scrollArea: 85,
    animationCurve: "easeInQuad"
}

/**
 * Automates scrolling when an active chunk resize is in progress and the mouse
 * approaches the top or bottom of the canvas.
 */
export class AutoScrollSystem implements ISystem {
    private readonly _scroller: MutableRefObject<HTMLDivElement | undefined>;
    private readonly _scrollState: Record<string, number>;
    private _isScrollInitialized = false;
    get updateEnabled() { return true; }

    dt!: number;
    input!: IInput;
    resources!: IResources;
    entities!: IEntityManager;
    queries: Queries = {
        resizes: { types: [ComponentId.Resize] },
        view: { types: [ComponentId.View] }
    }

    constructor(scroller: MutableRefObject<HTMLDivElement | undefined>, scrollState: Record<string, number>) {
        this._scroller = scroller;
        this._scrollState = scrollState;
    }

    update() {
        this.initializeScrollPosition();
        this.updateAutoScrollDrag();
    }

    initializeScrollPosition() {
        if (!this._isScrollInitialized && !!this._scroller.current && !!this.resources.get("worldSpace").value.height) {
            const value = this._scrollState[`${this.resources.get("documentId").value}:sdv`];
            this._scroller.current.scrollTop = value ?? 0;
            this._isScrollInitialized = true;
        }
    }

    updateAutoScrollDrag() {
        const mode = this.resources.get("mode");
        const cursor = this.input.getAxis2d(ActionId.SelectCursor);
        if (mode.value !== Mode.Edit || !cursor) {
            return;
        }

        let isAutoScrollActive = false;
        for (const entity of this.queries.resizes.query!.entities) {
            const resizeComp = entity.getComponent<ResizeComponent>(ComponentId.Resize)!;
            if (!!resizeComp.isCreating || !!resizeComp.isResizing) {
                isAutoScrollActive = true;
                break;
            }
        }

        if (!isAutoScrollActive) {
            return;
        }

        const view = this.queries.view.query!.single()?.getComponent<ViewComponent>(ComponentId.View);
        if (!view) {
            return;
        }

        const viewPosition = view.viewport.worldPosition;
        const viewHeight = view.viewport.worldHeight;
        const cursorViewPosition = worldToViewSpace(cursor, viewPosition);

        const isTopEdgeScroll = cursorViewPosition.y <= defaultConfig.scrollAreaCanvasEdgeOffset;
        const isBottomEdgeScroll = cursorViewPosition.y >= viewHeight - defaultConfig.scrollAreaCanvasEdgeOffset;
        const shouldScroll = isTopEdgeScroll || isBottomEdgeScroll;

        if (!shouldScroll) {
            return;
        }

        const scrollStartY = isTopEdgeScroll ? defaultConfig.scrollAreaCanvasEdgeOffset : viewHeight - defaultConfig.scrollAreaCanvasEdgeOffset;

        // Calculate the y position in scroll area space
        const scrollAreaY = clamp(Math.abs(cursorViewPosition.y - scrollStartY), 0, defaultConfig.scrollArea);
        const scrollAreaDt = scrollAreaY / defaultConfig.scrollArea;

        const animationCurve = animationCurves[defaultConfig.animationCurve];
        const animationDt = animationCurve(scrollAreaDt);
        const speed = interpolate(defaultConfig.minSpeed, defaultConfig.maxSpeed, animationDt) * this.dt;

        this._scroller.current?.scrollBy(0, isTopEdgeScroll ? -speed : speed)
    }
}

